import * as Immutable from 'immutable'
import * as moment from 'moment'
import * as React from 'react'
import { FormattedNumber, InjectedIntlProps, injectIntl } from 'react-intl'
import { AsyncState, none, Option, some } from 'widgets-for-react'
import { LoaderCircle } from '../../../accommodations/models/loading'
import { getCsrfToken } from '../../../utils/csrf'
import { prefixUrlWithLocale } from '../../../utils/url'
import { PageLoader } from '../../shared/loaders/PageLoader'

import * as intl from 'intl-messageformat'
import {
  PostRefundResponse,
  RefundingSpecification,
  RemoveEscrowResponse,
  Transaction,
  TransactionRefundingSpecification
} from './bookingDetailsApi'
import {
  AppPayment,
  ArrivalDateMutation,
  Booking,
  FeeSpecification,
  getRefundingErrors,
  LayoutProps
} from './bookingDetailsState'

const BookingExtendButton = (props: { identifier: string }) => (
  <form
    method="post"
    action={prefixUrlWithLocale(
      `/superadmin/bookings/${props.identifier}/extend_request`
    )}
    onSubmit={e => {
      if (window.confirm(I18n.t('superadmin.bookings.extend_confirmation'))) {
        return true
      }
      e.preventDefault()
      return false
    }}
  >
    <input name="_method" value="put" type="hidden" />
    <input name="authenticity_token" value={getCsrfToken()} type="hidden" />
    <input
      type="submit"
      value={I18n.t('superadmin.bookings.extend')}
      className="btn btn--small btn--red"
    />
  </form>
)

const BookingOverviewRow = (props: {
  label: string | JSX.Element
  children: string | JSX.Element
}) => (
  <tr>
    <td>{props.label}</td>
    <td>{props.children}</td>
  </tr>
)

const BookingOverview = (props: { booking: Booking }) => (
  <div className="box">
    <table className="table--bordered">
      <tbody>
        <BookingOverviewRow
          label={I18n.t('activerecord.attributes.conversation.tenant')}
        >
          <a
            href={prefixUrlWithLocale(
              `/superadmin/users/${props.booking.tenant.id}`
            )}
          >
            {props.booking.tenant.full_name}
          </a>
        </BookingOverviewRow>

        <BookingOverviewRow
          label={`${I18n.t('activerecord.attributes.booking.arrival_date')} -
          ${I18n.t('activerecord.attributes.booking.departure_date')}`}
        >
          <>
            {' '}
            {props.booking.arrival_date.format('DD MMM Y')} -{' '}
            {props.booking.departure_date.format('DD MMM Y')}{' '}
            <a
              className="btn btn--small btn--red"
              href={prefixUrlWithLocale(
                `/superadmin/bookings/${props.booking.identifier}/edit_dates`
              )}
            >
              {I18n.t('superadmin.bookings.edit_dates')}
            </a>
          </>
        </BookingOverviewRow>

        <BookingOverviewRow
          label={I18n.t('activerecord.attributes.rental_advert.object_name')}
        >
          <a
            href={prefixUrlWithLocale(
              `/superadmin/rentals/${props.booking.advert.id}`
            )}
          >
            {props.booking.advert.name}
          </a>
        </BookingOverviewRow>

        <BookingOverviewRow
          label={I18n.t('activerecord.attributes.conversation.owner')}
        >
          <a
            href={prefixUrlWithLocale(
              `/superadmin/users/${props.booking.owner.id}`
            )}
          >
            {props.booking.owner.full_name}
          </a>
        </BookingOverviewRow>

        {props.booking.payment_provider === 'opp' ? (
          <BookingOverviewRow
            label={I18n.t('superadmin.bookings.opp_merchant_uid')}
          >
            {!!props.booking.owner.opp_merchant_uid
              ? props.booking.owner.opp_merchant_uid
              : '-'}
          </BookingOverviewRow>
        ) : (
          <></>
        )}

        <BookingOverviewRow label={I18n.t('superadmin.bookings.booking_state')}>
          <>
            {props.booking.is_request_extendable ? (
              <BookingExtendButton identifier={props.booking.identifier} />
            ) : (
              <></>
            )}

            {props.booking.humanized_state_for_superadmin}

            {props.booking.replacement_booking.v.kind === 'r' ? (
              <>
                {' '}
                <a
                  href={prefixUrlWithLocale(
                    `/superadmin/bookings/${props.booking.replacement_booking.v.v}/detail`
                  )}
                  className="btn btn--small btn--white"
                >
                  {I18n.t('superadmin.bookings.replaced_by', {
                    identifier: props.booking.replacement_booking.v.v
                  })}
                </a>
              </>
            ) : (
              <></>
            )}
          </>
        </BookingOverviewRow>

        <BookingOverviewRow
          label={I18n.t('superadmin.bookings.payment_provider')}
        >
          {props.booking.payment_provider}
        </BookingOverviewRow>

        {props.booking.is_booking_completed ? (
          <>
            <BookingOverviewRow
              label={I18n.t(
                'activerecord.attributes.booking.amount_to_disburse'
              )}
            >
              <FormattedNumber
                value={props.booking.amount_to_disburse}
                style="currency"
                currency="EUR"
              />
            </BookingOverviewRow>

            <BookingOverviewRow
              label={I18n.t('activerecord.attributes.booking.disbursed')}
            >
              {I18n.t(
                `booleans.${props.booking.is_disbursed ? 'true' : 'false'}`
              )}
            </BookingOverviewRow>
          </>
        ) : (
          <></>
        )}
      </tbody>
    </table>
  </div>
)

const FeeSpecificationRow = (props: {
  label: string | JSX.Element
  tdClassName?: string
  children?: JSX.Element
  className?: string
}) => (
  <tr className={props.className}>
    <td className={`cell-33 ${props.tdClassName || ''}`}>{props.label}</td>
    <td className={props.tdClassName || ''}>{props.children}</td>
  </tr>
)

const FeeSpecificationAmountRow = (props: {
  label: string | JSX.Element
  className?: string
  tdClassName?: string
  amount: number
}) => (
  <FeeSpecificationRow
    label={props.label}
    className={props.className}
    tdClassName={props.tdClassName}
  >
    <FormattedNumber value={props.amount} style="currency" currency="EUR" />
  </FeeSpecificationRow>
)

const FeeSpecification = (props: {
  specification: FeeSpecification
  detailedRentalAmount: boolean
  toggleDetailedRentalAmount: () => void
}) => (
  <div className="box">
    <h2>{I18n.t('superadmin.bookings.fee_specification.label')}</h2>

    <h4>{I18n.t('activerecord.attributes.conversation.tenant')}</h4>
    <table className="table--bordered">
      <tbody>
        <FeeSpecificationAmountRow
          label={
            <>
              <a
                href=""
                onClick={e => {
                  e.preventDefault()
                  props.toggleDetailedRentalAmount()
                }}
              >
                {I18n.t('bookings.fees.rental_amount')}
                <i
                  className={
                    props.detailedRentalAmount
                      ? 'icon-chevron-down'
                      : 'icon-chevron-up'
                  }
                />
              </a>
            </>
          }
          amount={props.specification.commissionable_amount}
        />
        {props.detailedRentalAmount ? (
          <>
            <FeeSpecificationAmountRow
              label={I18n.t('bookings.fees.rental_amount')}
              className="italic"
              tdClassName="cell--grey"
              amount={props.specification.rental_amount}
            />
            <FeeSpecificationAmountRow
              label={I18n.t('bookings.fees.cleaning')}
              className="italic"
              tdClassName="cell--grey"
              amount={props.specification.cleaning_fee}
            />
            <FeeSpecificationAmountRow
              label={I18n.t('bookings.fees.linnen')}
              className="italic"
              tdClassName="cell--grey"
              amount={props.specification.linnen_fee}
            />
            <FeeSpecificationAmountRow
              label={I18n.t('bookings.fees.towels')}
              className="italic"
              tdClassName="cell--grey"
              amount={props.specification.towel_fee}
            />
            {props.specification.additional_fees.map(x => (
              <FeeSpecificationAmountRow
                label={I18n.t('bookings.fees.additional', {
                  description: x.description
                })}
                className="italic"
                tdClassName="cell--grey"
                amount={x.amount}
              />
            ))}
          </>
        ) : (
          <></>
        )}
        <FeeSpecificationAmountRow
          label={I18n.t('bookings.fees.tourist_tax')}
          amount={props.specification.tourist_tax_amount}
        />
        <FeeSpecificationAmountRow
          label={I18n.t('bookings.fees.booking')}
          amount={props.specification.booking_fee}
        />
      </tbody>
    </table>

    <h4>{I18n.t('activerecord.attributes.conversation.owner')}</h4>
    <table>
      <tbody>
        <FeeSpecificationAmountRow
          label={
            <>
              {props.specification.commission_discount_percentage > 0
                ? I18n.t('bookings.fees.commission_with_discount', {
                    percentage: `${props.specification.commission_percentage}%`,
                    discount: `${props.specification.commission_discount_percentage}%`
                  })
                : I18n.t('bookings.fees.commission', {
                    percentage: `${props.specification.commission_percentage}%`
                  })}
              {' x '}
              <FormattedNumber
                value={props.specification.commissionable_amount}
                style="currency"
                currency="EUR"
              />
            </>
          }
          amount={props.specification.commission_amount_ex_vat}
        />

        {props.specification.vat_percentage > 0 ? (
          <FeeSpecificationAmountRow
            label={I18n.t('bookings.fees.vat', {
              percentage: `${props.specification.vat_percentage}%`
            })}
            amount={props.specification.commission_vat_amount}
          />
        ) : (
          <FeeSpecificationRow label={I18n.t('bookings.fees.vat_deferred')} />
        )}
      </tbody>
    </table>
  </div>
)

const BookingArrivalDateHistory = (props: {
  history: ArrivalDateMutation[]
}) => (
  <div className="box">
    <h2>{I18n.t('superadmin.bookings.arrival_date_history.label')}</h2>
    {props.history.length > 1
      ? props.history.map(x => (
          <>
            {!!x.from ? ' → ' : ''}
            {x.to.format('DD MMM Y')}
          </>
        ))
      : I18n.t('superadmin.bookings.arrival_date_history.unknown')}
  </div>
)

const PaymentUids = (props: {
  payment: AppPayment
  transactionUids: string[]
}) => {
  return props.transactionUids.length > 0 ? (
    <>
      {props.payment.opp_transaction_uid.startsWith('mtr_') ? (
        <i>{props.payment.opp_transaction_uid}:</i>
      ) : (
        ''
      )}

      <ul>
        {props.transactionUids.map(x => (
          <li>{x}</li>
        ))}
      </ul>
    </>
  ) : (
    <></>
  )
}

const BookingPaymentsOverview = (props: {
  payments: AppPayment[]
  getTransactionUIds: Option<
    | {
        kind: 'loading'
      }
    | {
        kind: 'loaded'
        value: (_: AppPayment) => string[]
      }
  >
  loading: JSX.Element
}) => (
  <div className="box">
    <h2>{I18n.t('payments.title')}</h2>
    <table className="table--bordered">
      <thead>
        <tr>
          <th>{I18n.t('activerecord.attributes.payment.term')}</th>
          <th>
            {I18n.t('activerecord.attributes.payment.merchant_reference')}
          </th>
          {props.getTransactionUIds.v.kind === 'r' ? (
            <th>{I18n.t('superadmin.bookings.opp_transaction_uid')}</th>
          ) : (
            <></>
          )}
          <th>{I18n.t('activerecord.attributes.payment.paid')}</th>
          <th>{I18n.t('activerecord.attributes.payment.amount')}</th>
          <th>{I18n.t('activerecord.attributes.payment.due_date')}</th>
        </tr>
      </thead>
      <tbody>
        {Immutable.List(props.payments)
          .sortBy(x => x.due_date.unix())
          .map(x => (
            <tr>
              <td>{I18n.t(`payments.${x.i18n_key}.label`)}</td>
              <td>{x.merchant_reference}</td>
              {props.getTransactionUIds.v.kind === 'r' ? (
                <td>
                  {props.getTransactionUIds.v.v.kind === 'loaded' ? (
                    <PaymentUids
                      payment={x}
                      transactionUids={props.getTransactionUIds.v.v.value(x)}
                    />
                  ) : (
                    props.loading
                  )}
                </td>
              ) : (
                <></>
              )}
              <td>{I18n.t(`booleans.${x.is_paid ? 'true' : 'false'}`)}</td>
              <td>
                <FormattedNumber
                  value={x.amount}
                  style="currency"
                  currency="EUR"
                />
              </td>
              <td className="payment-term-form cell-20">
                {!!x.due_date ? x.due_date.format('DD MMM Y') : ''}
              </td>
            </tr>
          ))
          .toArray()}
      </tbody>
    </table>
  </div>
)

const TransactionsOverview = (props: {
  refundingSpecification: AsyncState<RefundingSpecification>
  buttonsDisabled: boolean
  termLabel: (_: TransactionRefundingSpecification) => string
  onRemoveEscrow: (_: Transaction) => void
  removeExcrow: AsyncState<RemoveEscrowResponse>
  loading: JSX.Element
}) => (
  <div className="box">
    <h2>{I18n.t('superadmin.bookings.opp_transactions.label')}</h2>
    {props.removeExcrow.kind === 'loaded' ? (
      <div className="box box--s box--bordered box--purple-200">
        <div className="box__body">
          {I18n.t('superadmin.bookings.refunding.remove_escrow_success')}
        </div>
      </div>
    ) : (
      <></>
    )}
    {props.removeExcrow.kind === 'failed' ? (
      <div className="box box--s box--bordered box--warning">
        <div className="box__body">
          {I18n.t('superadmin.bookings.refunding.remove_escrow_error')}
        </div>
      </div>
    ) : (
      <></>
    )}
    {props.refundingSpecification.kind === 'loaded' ? (
      props.refundingSpecification.value.transactions.map(t => (
        <div className="box" key={t.uid}>
          <h4>
            {t.uid} - {props.termLabel(t)}
          </h4>
          {!t.escrow_expired && t.has_escrow && t.can_remove_escrow ? (
            props.removeExcrow.kind === 'loading' ? (
              props.loading
            ) : (
              <button
                name="button"
                type="submit"
                className="btn btn--purple btn--small"
                onClick={() => props.onRemoveEscrow(t.transaction)}
                disabled={props.buttonsDisabled}
              >
                {I18n.t('superadmin.bookings.refunding.remove_escrow_label')}
              </button>
            )
          ) : (
            <></>
          )}
          <table className="table--bordered">
            <tbody>
              <FeeSpecificationAmountRow
                label={I18n.t('superadmin.bookings.opp_transactions.amount')}
                amount={t.amount / 100}
              />
              <FeeSpecificationAmountRow
                label={I18n.t(
                  'superadmin.bookings.opp_transactions.refunded_amount'
                )}
                amount={t.refunded_amount / 100}
              />

              <FeeSpecificationAmountRow
                label={I18n.t(
                  'superadmin.bookings.opp_transactions.refundable_amount'
                )}
                amount={t.refundable_amount / 100}
              />

              <FeeSpecificationRow
                label={I18n.t('superadmin.bookings.opp_transactions.escrow')}
              >
                <>{I18n.t(`booleans.${t.has_escrow ? 'true' : 'false'}`)}</>
              </FeeSpecificationRow>
              {t.has_escrow ? (
                <>
                  <FeeSpecificationRow
                    label={I18n.t(
                      'superadmin.bookings.opp_transactions.escrow_ends'
                    )}
                  >
                    <>{moment(t.escrow_ends_on).format('DD-MM-YYYY HH:mm')}</>
                  </FeeSpecificationRow>

                  <FeeSpecificationRow
                    label={I18n.t(
                      'superadmin.bookings.opp_transactions.escrow_ended'
                    )}
                  >
                    <>
                      {I18n.t(
                        `booleans.${t.escrow_expired ? 'true' : 'false'}`
                      )}
                    </>
                  </FeeSpecificationRow>
                </>
              ) : (
                <></>
              )}
            </tbody>
          </table>
        </div>
      ))
    ) : (
      <LoaderCircle />
    )}
  </div>
)

const RefundingFormRow = (props: {
  label: string | JSX.Element
  errors: string[]
  children: JSX.Element
}) => (
  <div className="form-row">
    <div className="form-cell">
      <div
        className={`input string required ${
          props.errors.length > 0 ? 'field_with_errors' : ''
        }`}
      >
        <div className="grid">
          <div className="grid-33 tablet-grid-33 mobile-grid-100">
            {props.label}
          </div>
          <div className="grid-66 tablet-grid-66 mobile-grid-100">
            {props.children}
            {props.errors.map(x => (
              <span key={x} className="error">
                {x}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
)

const RefundingForm = (props: {
  refundingSpecification: RefundingSpecification
  amount: number
  onAmountChange: (_: number) => void
  onSubmit: () => void
  buttonsDisabled: boolean
}) => {
  const errors = getRefundingErrors(props.refundingSpecification, props.amount)
  return (
    <>
      <RefundingFormRow
        label={
          <label
            className="number required control-label"
            htmlFor="refund_amount"
          >
            {I18n.t('superadmin.bookings.refunding.amount_label')}
          </label>
        }
        errors={errors.amount}
      >
        <input
          type="number"
          value={`${props.amount}`}
          onChange={x => props.onAmountChange(parseInt(x.currentTarget.value))}
          id="refund_amount"
        />
      </RefundingFormRow>

      <div className="form-row">
        <div className="form-cell">
          <div className="grid">
            <div className="grid-100">
              <button
                name="button"
                type="submit"
                className="btn btn btn--primary"
                onClick={() => props.onSubmit()}
                disabled={errors.amount.length > 0 || props.buttonsDisabled}
              >
                {I18n.t('superadmin.bookings.refunding.refund_label')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const RefundingOverview = (props: {
  refundingSpecification: AsyncState<RefundingSpecification>
}) => (
  <div className="box">
    <h2>{I18n.t('superadmin.bookings.refunding.label')}</h2>
    {props.refundingSpecification.kind === 'loaded' ? (
      <>
        <table className="table--bordered">
          <tbody>
            <FeeSpecificationAmountRow
              label={I18n.t('superadmin.bookings.refunding.in_escrow_amount')}
              amount={props.refundingSpecification.value.in_escrow_amount / 100}
            />
            <FeeSpecificationAmountRow
              label={I18n.t('superadmin.bookings.refunding.settlement_amount')}
              amount={
                props.refundingSpecification.value.settlement_amount / 100
              }
            />
            <FeeSpecificationAmountRow
              label={I18n.t('superadmin.bookings.refunding.refundable_amount')}
              tdClassName="cell--grey"
              className="larger"
              amount={
                props.refundingSpecification.value.refundable_amount / 100
              }
            />
            <FeeSpecificationAmountRow
              label={I18n.t('superadmin.bookings.refunding.refunded_amount')}
              tdClassName="cell--grey"
              className="larger"
              amount={
                props.refundingSpecification.value.transactions.reduce<number>(
                  (x, y) => (x += y.refunded_amount),
                  0
                ) / 100
              }
            />
          </tbody>
        </table>
      </>
    ) : (
      <LoaderCircle />
    )}
  </div>
)

const Refunding = (props: {
  refundingSpecification: AsyncState<RefundingSpecification>
  refund_amount: number
  onRefundAmountChange: (_: number) => void
  onRefundSubmit: () => void
  refunding: AsyncState<PostRefundResponse>
  buttonsDisabled: boolean
}) => (
  <>
    <RefundingOverview refundingSpecification={props.refundingSpecification} />
    {props.refunding.kind === 'loaded' &&
    props.refunding.value.kind === 'success' ? (
      <div className="box box--s box--bordered box--purple-200">
        <div className="box__body">
          {I18n.t('superadmin.bookings.refunding.refund_successful')}
        </div>
      </div>
    ) : (
      <></>
    )}
    {(props.refunding.kind === 'loaded' &&
      props.refunding.value.kind === 'error') ||
    props.refunding.kind === 'failed' ? (
      <div className="box box--s box--bordered box--warning">
        <div className="box__body">
          {props.refunding.kind === 'loaded' &&
          props.refunding.value.kind === 'error'
            ? `Error from OPP: ${props.refunding.value.value}`
            : 'unknown error'}
        </div>
      </div>
    ) : (
      <></>
    )}

    {props.refundingSpecification.kind === 'loaded' &&
    props.refundingSpecification.value.refundable_amount > 0 ? (
      <div className="box box--grey-100">
        <div className="box__body">
          {props.refunding.kind !== 'loading' ? (
            <RefundingForm
              refundingSpecification={props.refundingSpecification.value}
              amount={props.refund_amount}
              onAmountChange={props.onRefundAmountChange}
              onSubmit={props.onRefundSubmit}
              buttonsDisabled={props.buttonsDisabled}
            />
          ) : (
            <LoaderCircle />
          )}
        </div>
      </div>
    ) : (
      <></>
    )}
  </>
)

export const BookingLayout = injectIntl(
  (props: LayoutProps & InjectedIntlProps): JSX.Element => (
    <>
      {props.state.kind === 'loaded' ? (
        <>
          <BookingOverview booking={props.state.booking} />
          <FeeSpecification
            specification={props.state.booking.fee_specification}
            detailedRentalAmount={props.state.detailedRentalAmount}
            toggleDetailedRentalAmount={props.toggleDetailedRentalAmount}
          />
          <BookingArrivalDateHistory
            history={props.state.booking.arrival_date_history}
          />
          <BookingPaymentsOverview
            payments={props.state.booking.payments}
            getTransactionUIds={
              props.state.refunding.v.kind === 'l'
                ? none()
                : some(
                    props.state.refunding.v.v.specification.kind === 'loaded'
                      ? {
                          kind: 'loaded',
                          value: x =>
                            props.state.kind === 'loaded' &&
                            props.state.refunding.v.kind === 'r' &&
                            props.state.refunding.v.v.specification.kind ===
                              'loaded'
                              ? props.state.refunding.v.v.specification.value.transactions
                                  .filter(t => t.payment.Id === x.id)
                                  .map(t => t.uid)
                              : []
                        }
                      : {
                          kind: 'loading'
                        }
                  )
            }
            loading={<img src={props.loading_gif} />}
          />

          {props.state.refunding.v.kind === 'r' &&
          props.state.booking.payments.filter(x => x.is_paid).length > 0 ? (
            <>
              <Refunding
                refundingSpecification={props.state.refunding.v.v.specification}
                refund_amount={props.state.refunding.v.v.amount}
                onRefundAmountChange={props.onRefundAmountChange}
                buttonsDisabled={
                  props.state.refunding.v.v.removeEscrow.kind === 'loading' ||
                  props.state.refunding.v.v.refunding.kind === 'loading'
                }
                onRefundSubmit={() => {
                  if (
                    props.state.kind !== 'loaded' ||
                    props.state.refunding.v.kind === 'l'
                  ) {
                    return
                  }

                  if (
                    confirm(
                      I18n.t('superadmin.bookings.refunding.confirm', {
                        amount: props.intl.formatNumber(
                          props.state.refunding.v.v.amount / 100,
                          {
                            currency: 'EUR',
                            style: 'currency'
                          }
                        )
                      })
                    )
                  ) {
                    props.onRefundSubmit()
                  }
                }}
                refunding={props.state.refunding.v.v.refunding}
              />
              <TransactionsOverview
                refundingSpecification={props.state.refunding.v.v.specification}
                termLabel={t => {
                  if (props.state.kind !== 'loaded') {
                    return
                  }
                  const payment = props.state.booking.payments.find(
                    p => p.id === t.payment.Id
                  )

                  return !!payment
                    ? I18n.t(`payments.${payment.i18n_key}.label`)
                    : ''
                }}
                onRemoveEscrow={props.onRemoveEscrow}
                buttonsDisabled={
                  props.state.refunding.v.v.removeEscrow.kind === 'loading' ||
                  props.state.refunding.v.v.refunding.kind === 'loading'
                }
                removeExcrow={props.state.refunding.v.v.removeEscrow}
                loading={<img src={props.loading_gif} />}
              />
            </>
          ) : (
            <></>
          )}
        </>
      ) : props.state.booking.kind === 'loading' ? (
        <PageLoader />
      ) : (
        <>Something went wrong, try again later</>
      )}
    </>
  )
)
